import { tallyTokens } from "../../theme.tsx";
import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import { DashboardTile } from "../../store/types.tsx";
import CustomReactMarkDown from "../../utils/markdown-wrapper.tsx";
import BarGraph, { validateBarGraph } from "../graphs/BarGraph.tsx";
import ComboGraph, { validateComboGraph } from "../graphs/ComboGraph.tsx";
import LineGraph, { validateLineGraph } from "../graphs/LineGraph.tsx";
import InvalidGraph from "../graphs/InvalidGraph.tsx";
import { GraphErrorBoundary } from "./GraphErrorBoundary.tsx";
import TableContent from "./TableContent.tsx";
import { i18n } from "@lingui/core";
import { msg, t } from "@lingui/core/macro";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import {
  deleteDashboardTile,
  moveDashboardTile,
  refreshDashboardTile,
  updateDashboardTilePrompt,
} from "../../store/conversation.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import TallyDialog from "../dialog/TallyDialog.tsx";

interface DashboardTileCardProps {
  tile: DashboardTile;
  fillHeight?: boolean;
  /** Renders the tile without its edit, menu and drag affordances, e.g. for
   *  the PDF export. */
  readOnly?: boolean;
}

type GraphTileContent = {
  data: Array<{ [key: string]: any }>;
  graph_type: string;
  x_axis?: string;
  y_axis?: string;
  series_types?: Record<string, "bar" | "line">;
  clarification?: string;
};

type TableTileContent = {
  rows: Array<{ [key: string]: any }>;
  columns: GridColDef[];
  clarification?: string;
};

const NEW_DASHBOARD_VALUE = "__new_dashboard__";
/** Below this a chart's axes and legend crowd out the data. */
const MIN_TILE_CHART_HEIGHT = 220;

const DashboardTileCard: React.FC<DashboardTileCardProps> = ({
  tile,
  fillHeight = false,
  readOnly = false,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { dashboardIds, dashboardsById } = useTypedSelector(
    (state) => state.conversationState,
  );
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [moveOpen, setMoveOpen] = React.useState(false);
  const [removeOpen, setRemoveOpen] = React.useState(false);
  const [expandOpen, setExpandOpen] = React.useState(false);
  const [editTilePromptOpen, setEditTilePromptOpen] = React.useState(false);
  const [selectedDashboardId, setSelectedDashboardId] = React.useState("");
  const [newDashboardTitle, setNewDashboardTitle] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const tileContent =
    tile.json_response?.content !== undefined
      ? tile.json_response.content
      : tile.data;
  const tileType = tile.json_response?.type || tile.type || "TextResponse";
  const availableDashboards = React.useMemo(
    () =>
      dashboardIds.filter((dashboardId) => dashboardId !== tile.dashboard_id),
    [dashboardIds, tile.dashboard_id],
  );

  React.useEffect(() => {
    setSelectedDashboardId((currentValue) => {
      if (availableDashboards.length === 0) {
        return NEW_DASHBOARD_VALUE;
      }

      return currentValue && availableDashboards.includes(currentValue)
        ? currentValue
        : availableDashboards[0];
    });
  }, [availableDashboards]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => setMenuAnchorEl(null);

  const handleMoveTile = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(
        moveDashboardTile({
          tileId: tile.id,
          targetDashboardId:
            selectedDashboardId !== NEW_DASHBOARD_VALUE
              ? selectedDashboardId
              : undefined,
          targetDashboardTitle:
            selectedDashboardId === NEW_DASHBOARD_VALUE
              ? newDashboardTitle.trim()
              : undefined,
          sourceExchangeId: tile.source_exchange_id,
          sourceDashboardId: tile.dashboard_id,
        }) as any,
      );
      setMoveOpen(false);
      setMenuAnchorEl(null);
      setNewDashboardTitle("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTile = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(
        deleteDashboardTile({
          tileId: tile.id,
          sourceExchangeId: tile.source_exchange_id,
          dashboardId: tile.dashboard_id,
        }) as any,
      );
      setRemoveOpen(false);
      setMenuAnchorEl(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefreshTile = async () => {
    setIsSubmitting(true);
    try {
      await dispatch(refreshDashboardTile(tile.id) as any);
      setMenuAnchorEl(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTilePrompt = async (tilePrompt: string) => {
    setIsSubmitting(true);
    try {
      await dispatch(
        updateDashboardTilePrompt({
          tileId: tile.id,
          tilePrompt,
        }) as any,
      );
      setEditTilePromptOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // In the grid a tile has a fixed height, so its chart is sized to the space
  // left under the title instead of the fixed conversation height. This is
  // what lets charts grow with the tile on large screens.
  const [chartAreaNode, setChartAreaNode] =
    React.useState<HTMLDivElement | null>(null);
  const [chartAreaHeight, setChartAreaHeight] = React.useState<number | null>(
    null,
  );

  React.useEffect(() => {
    if (!chartAreaNode || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const height = Math.floor(entries[0]?.contentRect.height ?? 0);
      setChartAreaHeight(height > 0 ? height : null);
    });
    observer.observe(chartAreaNode);
    return () => observer.disconnect();
  }, [chartAreaNode]);

  const renderGraph = (graphContent: GraphTileContent, fitToTile: boolean) => {
    const { data, graph_type, x_axis, y_axis, series_types, clarification } =
      graphContent;

    const height =
      fitToTile && chartAreaHeight
        ? Math.max(MIN_TILE_CHART_HEIGHT, chartAreaHeight)
        : undefined;

    const graphNode =
      graph_type === "combo" &&
      validateComboGraph(data, x_axis, series_types) ? (
        <ComboGraph
          content={data}
          xAxisKey={x_axis || ""}
          yAxisKey={y_axis || ""}
          seriesTypes={series_types || {}}
          height={height}
        />
      ) : graph_type === "line" && validateLineGraph(data, x_axis) ? (
        <LineGraph
          content={data}
          xAxisKey={x_axis || ""}
          yAxisKey={y_axis}
          height={height}
        />
      ) : graph_type === "bar" && validateBarGraph(data, x_axis) ? (
        <BarGraph
          content={data}
          xAxisKey={x_axis || ""}
          yAxisKey={y_axis || ""}
          height={height}
        />
      ) : (
        <InvalidGraph />
      );

    if (fitToTile) {
      return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            ref={setChartAreaNode}
            sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}
          >
            <GraphErrorBoundary>{graphNode}</GraphErrorBoundary>
          </Box>
          {clarification && (
            <Box sx={{ marginTop: 1.5 }}>
              <CustomReactMarkDown content={clarification} compact />
            </Box>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <GraphErrorBoundary>{graphNode}</GraphErrorBoundary>
        {clarification && (
          <Box sx={{ marginTop: 1.5 }}>
            <CustomReactMarkDown content={clarification} compact />
          </Box>
        )}
      </Box>
    );
  };

  const renderTable = (tableContent: TableTileContent) => (
    <TableContent
      content={tableContent}
      clarificationCompact
      gridWrapperSx={{
        width: "100%",
        overflowX: "auto",
      }}
      invalidContent={
        <Typography variant="body2" color="error">
          {i18n._(t`Invalid table data.`)}
        </Typography>
      }
      gridOptions={{
        autoHeight: true,
        disableRowSelectionOnClick: true,
        disableColumnMenu: true,
        hideFooter: tableContent.rows.length <= 5,
        initialPageSize: 5,
        pageSizeOptions: [5],
        columnMinWidth: 140,
      }}
    />
  );

  const renderTileContent = (fitToTile: boolean) => {
    if (
      tileType === "Graph" &&
      tileContent &&
      typeof tileContent === "object"
    ) {
      return renderGraph(tileContent as GraphTileContent, fitToTile);
    }

    if (
      tileType === "Table" &&
      tileContent &&
      typeof tileContent === "object"
    ) {
      return renderTable(tileContent as TableTileContent);
    }

    return <CustomReactMarkDown content={String(tileContent || "")} compact />;
  };

  const dialogPaperSx = {
    borderRadius: theme.spacing(2),
    marginLeft: isDesktop ? `calc(${SETTINGS.drawerWidth}px)` : "auto",
    paddingBottom: theme.spacing(2.5),
  };

  // `fitToTile` is off in the expand dialog, which has no fixed height.
  const renderTileBody = (showActions = true, fitToTile = fillHeight) => (
    <React.Fragment>
      <Box
        className={showActions ? "dashboard-tile-drag-handle" : undefined}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          marginBottom: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              lineHeight: 1.35,
            }}
          >
            {tile.title}
          </Typography>
        </Box>
        {showActions && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              className="dashboard-tile-action"
              size="small"
              onClick={() => setEditTilePromptOpen(true)}
              disabled={isSubmitting}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              className="dashboard-tile-action"
              size="small"
              onClick={handleOpenMenu}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          flex: fillHeight ? 1 : "unset",
          minHeight: 0,
          overflow: fillHeight ? "auto" : "visible",
          paddingRight: fillHeight ? 0.25 : 0,
        }}
      >
        {renderTileContent(fitToTile)}
      </Box>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <Card
        elevation={0}
        sx={{
          height: fillHeight ? "100%" : "auto",
          borderRadius: "16px",
          backgroundColor: tallyTokens.surface,
          border: `1px solid ${tallyTokens.line}`,
          backdropFilter: "blur(8px)",
          overflow: "hidden",
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0,
            height: fillHeight ? "100%" : "auto",
            padding: 2.5,
            "&:last-child": {
              paddingBottom: 2.5,
            },
          }}
        >
          {renderTileBody(!readOnly)}
        </CardContent>
      </Card>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            setExpandOpen(true);
            handleCloseMenu();
          }}
        >
          <OpenInFullIcon sx={{ mr: 1, fontSize: 18 }} />
          {i18n._(t`Expand`)}
        </MenuItem>
        <MenuItem onClick={handleRefreshTile} disabled={isSubmitting}>
          <RefreshIcon sx={{ mr: 1, fontSize: 18 }} />
          {i18n._(t`Refresh`)}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMoveOpen(true);
            handleCloseMenu();
          }}
        >
          <DriveFileMoveOutlinedIcon sx={{ mr: 1, fontSize: 18 }} />
          {i18n._(t`Move to dashboard`)}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRemoveOpen(true);
            handleCloseMenu();
          }}
        >
          <DeleteOutlineIcon sx={{ mr: 1, fontSize: 18 }} />
          {i18n._(t`Remove from dashboard`)}
        </MenuItem>
      </Menu>

      <Dialog
        open={expandOpen}
        onClose={() => setExpandOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ px: { xs: 0, md: 1 }, pb: 1 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: "16px",
                backgroundColor: tallyTokens.surface,
                border: `1px solid ${tallyTokens.line}`,
                backdropFilter: "blur(8px)",
              }}
            >
              <CardContent
                sx={{
                  padding: { xs: 2.5, md: 4 },
                  "&:last-child": {
                    paddingBottom: { xs: 2.5, md: 4 },
                  },
                }}
              >
                {renderTileBody(false, false)}
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ mr: 2 }}>
          <Button
            variant="outlined"
            sx={{ borderRadius: theme.spacing(2) }}
            onClick={() => setExpandOpen(false)}
          >
            {i18n._(t`Close`)}
          </Button>
        </DialogActions>
      </Dialog>

      <TallyDialog
        open={editTilePromptOpen}
        title={i18n._(
          msg({
            context:
              "Title of a dialog enabling the user to edit the dashboard tile prompt.",
            message: "Edit tile prompt",
          }),
        )}
        subtitle={i18n._(
          msg({
            context:
              "Subtitle of a dialog explaining that editing the dashboard tile prompt only affects the tile refresh logic.",
            message:
              "Update the prompt used to regenerate this tile. This will not affect the original exchange.",
          }),
        )}
        submitTitle={i18n._(
          msg({
            context: "Button title of the edit dashboard tile prompt dialog.",
            message: "Save",
          }),
        )}
        initialInputValue={tile.tile_prompt}
        onSubmit={handleEditTilePrompt}
        handleDialogClose={() => setEditTilePromptOpen(false)}
      />

      <Dialog
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent>
          <DialogTitle
            sx={{
              color: theme.palette.primary.main,
              paddingLeft: 0,
            }}
          >
            {i18n._(t`Move tile`)}
          </DialogTitle>
          <DialogContentText
            sx={{
              color: theme.palette.text.disabled,
              paddingBottom: theme.spacing(1),
            }}
          >
            {i18n._(
              t`Choose another dashboard for this tile, or create a new one.`,
            )}
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <InputLabel id={`move-dashboard-${tile.id}`}>
              {i18n._(t`Dashboard`)}
            </InputLabel>
            <Select
              labelId={`move-dashboard-${tile.id}`}
              label={i18n._(t`Dashboard`)}
              value={selectedDashboardId}
              onChange={(event) =>
                setSelectedDashboardId(event.target.value as string)
              }
            >
              {availableDashboards.map((dashboardId) => (
                <MenuItem key={dashboardId} value={dashboardId}>
                  {dashboardsById[dashboardId]?.title}
                </MenuItem>
              ))}
              <MenuItem value={NEW_DASHBOARD_VALUE}>
                {i18n._(t`Create new dashboard`)}
              </MenuItem>
            </Select>
          </FormControl>
          {selectedDashboardId === NEW_DASHBOARD_VALUE && (
            <TextField
              fullWidth
              sx={{ mt: 2 }}
              label={i18n._(t`Dashboard title`)}
              value={newDashboardTitle}
              onChange={(event) => setNewDashboardTitle(event.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ mr: 2 }}>
          <Button
            variant="outlined"
            sx={{ borderRadius: theme.spacing(2) }}
            onClick={() => setMoveOpen(false)}
          >
            {i18n._(t`Cancel`)}
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: theme.spacing(2) }}
            onClick={handleMoveTile}
            disabled={
              isSubmitting ||
              (selectedDashboardId === NEW_DASHBOARD_VALUE &&
                newDashboardTitle.trim() === "")
            }
          >
            {i18n._(t`Move`)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent>
          <DialogTitle
            sx={{
              color: theme.palette.primary.main,
              paddingLeft: 0,
            }}
          >
            {i18n._(t`Remove tile?`)}
          </DialogTitle>
          <DialogContentText
            sx={{
              color: theme.palette.text.disabled,
              paddingBottom: theme.spacing(1),
            }}
          >
            {i18n._(t`This will remove the tile from the current dashboard.`)}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ mr: 2 }}>
          <Button
            variant="outlined"
            sx={{ borderRadius: theme.spacing(2) }}
            onClick={() => setRemoveOpen(false)}
          >
            {i18n._(t`Cancel`)}
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ borderRadius: theme.spacing(2) }}
            onClick={handleDeleteTile}
            disabled={isSubmitting}
          >
            {i18n._(t`Remove`)}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default DashboardTileCard;
